import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetList, useNotify, useTranslate } from "ra-core";

import { Label } from "@/components/ui/label";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import type { Sale } from "@/components/atomic-crm/types";
import { nativeSelectClass } from "../tasks/taskData";

const KEY = "deals_default_owner";

// Who gets the automatic follow-ups of deals without an owner (task 1.8,
// D-037): app_settings.deals_default_owner, edited by the admin and saved
// on change. "Nobody" removes it: the follow-ups then have no owner and the
// job reports them in integration_runs.
export const DealsDefaultOwnerSetting = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
    filter: { disabled: false },
  });
  const { data: ownerId } = useQuery({
    queryKey: ["app_settings", KEY],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("app_settings")
        .select("value")
        .eq("key", KEY)
        .maybeSingle();
      if (error) throw error;
      return typeof data?.value === "number" ? data.value : null;
    },
  });

  const save = async (value: string) => {
    const table = getSupabaseClient().from("app_settings");
    const { error } = value
      ? await table.upsert({ key: KEY, value: Number(value) })
      : await table.delete().eq("key", KEY);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["app_settings", KEY] });
  };

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="tenax-deals-default-owner">
        {translate("tenax.deals.default_owner.label")}
      </Label>
      <select
        id="tenax-deals-default-owner"
        className={nativeSelectClass}
        value={ownerId ?? ""}
        onChange={(event) => save(event.target.value)}
      >
        <option value="">{translate("tenax.deals.default_owner.none")}</option>
        {sales.map((sale) => (
          <option key={sale.id} value={sale.id}>
            {sale.first_name} {sale.last_name}
          </option>
        ))}
      </select>
      <p className="text-xs text-muted-foreground">
        {translate("tenax.deals.default_owner.hint")}
      </p>
    </div>
  );
};
